<script lang="ts" module>
  import type { Component } from 'svelte';
  export interface FilterOption<T> {
    value: T;
    label: string;
    sublabel?: string;
    /** Colour dot (client, activity). */
    color?: string;
    icon?: Component<any>;
    disabled?: boolean;
  }
</script>

<script lang="ts" generics="T extends string | number">
  // Filter pill with a searchable option list (the one filter control of the app).
  //   <FilterSelect label="Klant" options={clientOptions} bind:value={clientIds} />              multi, "Klant: Acme"
  //   <FilterSelect label="Status" options={statuses} bind:value={status} multiple={false} />   single choice
  //   <FilterSelect label="Klanten" allLabel="Alle klanten" icon={Users} … />                  "Alle klanten ▾"
  //   <FilterSelect label="Klant" inline … />                                                  inline list (mobile filter sheet)
  // `value` is always an array (single mode: [] or [v]). Search shows from 8
  // options (searchable="auto"). Keyboard: ↑/↓ move, Enter/Space toggles, Esc closes.
  import { ChevronDown, X, Search, Check } from '@lucide/svelte';
  import Popover from './Popover.svelte';
  import ColorDot from './ColorDot.svelte';
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props {
    label: string;
    options: FilterOption<T>[];
    value?: T[];
    multiple?: boolean;
    icon?: Component<any>;
    /** Trigger text while nothing is selected ("Alle klanten"); the trigger then shows only the selection. */
    allLabel?: string;
    /** Trigger text for several selected values (default "{n} geselecteerd"). */
    summary?: (count: number) => string;
    searchable?: boolean | 'auto';
    /** Render the list inline (filter sheets) instead of a pill + popover. */
    inline?: boolean;
    /** Show the × / "Wissen" controls (default true). */
    clearable?: boolean;
    size?: 'sm' | 'md';
    placement?: 'bottom-start' | 'bottom-end';
    width?: string;
    disabled?: boolean;
    emptyLabel?: string;
    onchange?: (value: T[]) => void;
    class?: string;
  }
  let {
    label,
    options,
    value = $bindable([]),
    multiple = true,
    icon: Icon,
    allLabel,
    summary,
    searchable = 'auto',
    inline = false,
    clearable = true,
    size = 'sm',
    placement = 'bottom-start',
    width = '280px',
    disabled = false,
    emptyLabel,
    onchange,
    class: cls = ''
  }: Props = $props();

  const listId = `fs-${Math.random().toString(36).slice(2, 8)}`;
  let open = $state(false);
  let anchor: HTMLElement | null = $state(null);
  let trigger: HTMLButtonElement | null = $state(null);
  let panel: HTMLElement | null = $state(null);
  let query = $state('');

  const selected = $derived(new Set<T>(value));
  const active = $derived(value.length > 0);
  const showSearch = $derived(searchable === 'auto' ? options.length > 7 : searchable);
  const text = $derived.by(() => {
    if (!value.length) return allLabel ?? label;
    if (value.length === 1) return options.find((o) => o.value === value[0])?.label ?? String(value[0]);
    return summary ? summary(value.length) : t('ui.n_selected', { count: value.length });
  });
  const filtered = $derived.by(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLocaleLowerCase().includes(q) || (o.sublabel || '').toLocaleLowerCase().includes(q));
  });

  function set(next: T[]) {
    value = next;
    onchange?.(next);
  }

  function toggle(o: FilterOption<T>) {
    if (o.disabled) return;
    if (!multiple) {
      set(selected.has(o.value) && clearable ? [] : [o.value]);
      if (!inline) close();
      return;
    }
    set(selected.has(o.value) ? value.filter((v) => v !== o.value) : [...value, o.value]);
  }

  function close() {
    open = false;
    trigger?.focus();
  }

  function keynav(event: KeyboardEvent) {
    if (event.key === 'Escape' && !inline) {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const root = event.currentTarget as HTMLElement;
    const items = Array.from(root.querySelectorAll<HTMLElement>('input, button.opt:not(:disabled)'));
    if (!items.length) return;
    event.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = event.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  }

  $effect(() => {
    if (!open) {
      query = '';
      return;
    }
    queueMicrotask(() => {
      const target = panel?.querySelector<HTMLElement>('input') ?? panel?.querySelector<HTMLElement>('button.opt[aria-selected="true"]') ?? panel?.querySelector<HTMLElement>('button.opt');
      target?.focus();
    });
  });
</script>

{#snippet list()}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="panel" bind:this={panel} onkeydown={keynav}>
    {#if showSearch}
      <label class="search">
        <Search aria-hidden="true" />
        <input type="text" bind:value={query} placeholder={t('ui.search_placeholder')} aria-label={t('ui.search')} aria-controls={listId} autocomplete="off" />
      </label>
    {/if}
    <div class="opts" id={listId} role="listbox" aria-multiselectable={multiple} aria-label={label}>
      {#each filtered as o (o.value)}
        {@const on = selected.has(o.value)}
        <button type="button" class="opt" role="option" aria-selected={on} disabled={o.disabled} onclick={() => toggle(o)}>
          {#if multiple}
            <span class="box" class:on aria-hidden="true">{#if on}<Check strokeWidth={3} />{/if}</span>
          {/if}
          {#if o.color}<ColorDot color={o.color} />{/if}
          {#if o.icon}<o.icon class="opt-icon" aria-hidden="true" />{/if}
          <span class="txt">
            <span class="lbl">{o.label}</span>
            {#if o.sublabel}<span class="sub">{o.sublabel}</span>{/if}
          </span>
          {#if !multiple && on}<Check class="chk" aria-hidden="true" />{/if}
        </button>
      {:else}
        <div class="empty">{emptyLabel ?? t('ui.no_results')}</div>
      {/each}
    </div>
    {#if multiple && !inline && (active || options.length > 7)}
      <div class="foot">
        {#if clearable}
          <button type="button" class="link" disabled={!active} onclick={() => set([])}>{t('ui.clear')}</button>
        {:else}<span></span>{/if}
        <button type="button" class="link brand" onclick={close}>{t('ui.done')}</button>
      </div>
    {/if}
  </div>
{/snippet}

{#if inline}
  <div class="inline {cls}">
    <div class="inline-head">
      <span class="inline-label">{label}</span>
      {#if active && clearable}<button type="button" class="link" onclick={() => set([])}>{t('ui.clear')}</button>{/if}
    </div>
    <div class="inline-box">{@render list()}</div>
  </div>
{:else}
  <span class="pill {size} {cls}" class:active class:select={!!allLabel} class:disabled bind:this={anchor}>
    <button
      type="button"
      class="main"
      bind:this={trigger}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-label={active ? `${label}: ${text}` : undefined}
      {disabled}
      onclick={() => (open = !open)}
    >
      {#if Icon}<Icon class="lead-icon" aria-hidden="true" />{/if}
      {#if active && !allLabel}<span class="k">{label}</span>{/if}
      <span class="v" class:placeholder={!active}>{text}</span>
      {#if !active || !clearable}<ChevronDown class="chev" aria-hidden="true" />{/if}
    </button>
    {#if active && clearable}
      <button type="button" class="x" aria-label={t('ui.clear_filter', { name: label })} {disabled} onclick={() => set([])}><X /></button>
    {/if}
  </span>
  <Popover bind:open {anchor} {placement} {width}>
    {@render list()}
  </Popover>
{/if}

<style>
  /* Trigger pill */
  .pill {
    display: inline-flex;
    align-items: center;
    height: var(--control-h-sm);
    max-width: 280px;
    min-width: 0;
    border: 1px dashed var(--border-strong);
    border-radius: var(--radius-pill);
    background: var(--surface);
    color: var(--text-2);
    font-size: var(--text-sm);
    transition: border-color var(--dur), background var(--dur), box-shadow var(--dur);
  }
  .md { height: var(--control-h); font-size: var(--text-base); }
  .select { border-style: solid; border-color: var(--border); }
  .pill:hover:not(.disabled) { border-color: var(--text-3); }
  .select:hover:not(.disabled) { border-color: var(--border-strong); }
  .pill:has(.main:focus-visible) { box-shadow: var(--ring); border-color: var(--brand); }
  .pill.active { border-style: solid; border-color: color-mix(in srgb, var(--brand) 35%, var(--border)); background: var(--brand-softer); color: var(--text); }
  .disabled { opacity: 0.55; }
  .main {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1_5);
    height: 100%;
    min-width: 0;
    padding: 0 var(--space-2_5) 0 var(--space-3);
    border: 0;
    border-radius: inherit;
    background: none;
    color: inherit;
    font: inherit;
    font-weight: var(--weight-medium);
  }
  .main:focus-visible { outline: none; }
  .active .main { padding-right: var(--space-1); }
  .active:not(:has(.x)) .main { padding-right: var(--space-2_5); }
  .main :global(.lead-icon) { width: var(--icon-sm); height: var(--icon-sm); color: var(--text-3); }
  .active .main :global(.lead-icon) { color: var(--brand); }
  .main :global(.chev) { width: var(--icon-sm); height: var(--icon-sm); color: var(--text-3); }
  .k { white-space: nowrap; color: var(--text-2); }
  .k::after { content: ':'; }
  .v { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .active .v { color: var(--brand); font-weight: var(--weight-semibold); }
  .v.placeholder { color: inherit; }
  .x {
    width: 22px;
    height: 22px;
    margin-right: var(--space-1);
    padding: 0;
    border: 0;
    border-radius: var(--radius-full);
    background: transparent;
    color: var(--text-3);
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .x :global(svg) { width: var(--icon-xs); height: var(--icon-xs); }
  .x:hover { background: var(--surface-hover); color: var(--text); }
  .x:focus-visible { outline: none; box-shadow: var(--ring); }

  /* Panel */
  .panel { display: flex; flex-direction: column; min-width: 0; }
  .search {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    border-bottom: 1px solid var(--border);
    color: var(--text-3);
  }
  .search :global(svg) { width: var(--icon-sm); height: var(--icon-sm); }
  .search input { flex: 1; min-width: 0; border: 0; padding: 0; background: transparent; outline: none; font-size: var(--text-sm); color: var(--text); }
  .search input::placeholder { color: var(--text-3); }
  .opts { padding: var(--space-1); display: flex; flex-direction: column; max-height: 300px; overflow-y: auto; }
  .opt {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    min-height: 34px;
    padding: var(--space-1_5) var(--space-2);
    border: 0;
    border-radius: var(--radius-xs);
    background: transparent;
    color: var(--text);
    font-size: var(--text-sm);
    text-align: left;
  }
  .opt:hover, .opt:focus-visible { background: var(--surface-hover); outline: none; }
  .opt:disabled { opacity: 0.5; }
  .box {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border: 1.5px solid var(--border-strong);
    border-radius: var(--radius-2xs);
    background: var(--surface);
    color: var(--on-brand);
    transition: background var(--dur), border-color var(--dur);
  }
  .box :global(svg) { width: 11px; height: 11px; }
  .box.on { background: var(--brand); border-color: var(--brand); }
  .opt :global(.opt-icon) { width: var(--icon-md); height: var(--icon-md); color: var(--text-2); }
  .txt { flex: 1; display: flex; flex-direction: column; min-width: 0; }
  .lbl, .sub { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sub { font-size: var(--text-xs); color: var(--text-3); }
  .opt :global(.chk) { width: var(--icon-md); height: var(--icon-md); color: var(--brand); }
  .opt[aria-selected='true']:not(:has(.box)) .lbl { font-weight: var(--weight-semibold); color: var(--brand); }
  .empty { padding: var(--space-3); text-align: center; color: var(--text-3); font-size: var(--text-sm); }
  .foot { display: flex; justify-content: space-between; padding: var(--space-1_5) var(--space-2); border-top: 1px solid var(--border); }
  .link {
    border: 0;
    background: none;
    padding: var(--space-1) var(--space-1_5);
    border-radius: var(--radius-xs);
    color: var(--text-2);
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
  }
  .link.brand { color: var(--brand); }
  .link:hover:not(:disabled) { background: var(--surface-hover); }
  .link:disabled { opacity: 0.45; }
  .link:focus-visible { outline: none; box-shadow: var(--ring); }

  /* Inline (filter sheets) */
  .inline { display: flex; flex-direction: column; gap: var(--space-1_5); min-width: 0; }
  .inline-head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); }
  .inline-label { font-size: var(--text-sm); font-weight: var(--weight-semibold); color: var(--text); }
  .inline-box { border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--surface); overflow: hidden; }
  .inline-box .opts { max-height: 220px; }
</style>
